import { IntlShape, MessageDescriptor } from 'react-intl';
import { isFullVideoTheme, isStudioThemeId, StudioThemeId } from 'fym-shared/src/music24/studioThemes';
import { CatalogMasterFailReason, KitAssetFailCode } from '../../../../redux/api/fymAPI.types';
import { studioMessages } from './studioMessages';
import { fullVideoProgressMessages } from './fullVideoProgressMessages';
import { aiBudgetMessages } from '../aiBudgetMessages';

/** The copy of each code. The old codes say that no credit was used (designs e3, m2b, n5). */
const FAIL_COPY: Record<KitAssetFailCode, MessageDescriptor> = {
  RENDER_TIMEOUT: studioMessages.failRender,
  ANALYSIS_FAILED: studioMessages.failAnalysis,
  SPEC_CHECK_FAILED: studioMessages.failSpec,
  PLAN_FAILED: studioMessages.failPlan,
  STALLED: studioMessages.failStalled,
  RENDER_FAILED: fullVideoProgressMessages.failRenderFailed,
  LYRICS_NOT_SYNCED: fullVideoProgressMessages.failLyricsNotSynced,
  BANK_TOO_SMALL: fullVideoProgressMessages.failBankTooSmall,
  AI_BUDGET_EXHAUSTED: aiBudgetMessages.failSentence,
};

/**
 * The copy of each code with no credit sentence (design s10). The refund line ("Your 3 credits
 * came back.") follows it.
 */
const PLAIN_COPY: Record<KitAssetFailCode, MessageDescriptor> = {
  RENDER_TIMEOUT: fullVideoProgressMessages.failTooLong,
  ANALYSIS_FAILED: fullVideoProgressMessages.failAnalysis,
  SPEC_CHECK_FAILED: fullVideoProgressMessages.failSpec,
  PLAN_FAILED: fullVideoProgressMessages.failPlan,
  STALLED: fullVideoProgressMessages.failStalled,
  RENDER_FAILED: fullVideoProgressMessages.failRenderFailed,
  LYRICS_NOT_SYNCED: fullVideoProgressMessages.failLyricsNotSynced,
  BANK_TOO_SMALL: fullVideoProgressMessages.failBankTooSmall,
  AI_BUDGET_EXHAUSTED: aiBudgetMessages.failSentence,
};

/** The codes of the full videos (spec §9.3). Their copy never says how many credits came back. */
const NEW_CODES: ReadonlyArray<KitAssetFailCode> = [
  'RENDER_FAILED',
  'LYRICS_NOT_SYNCED',
  'BANK_TOO_SMALL',
  'AI_BUDGET_EXHAUSTED',
];

/** True when the asset failed because the Free AI budget is spent: the row offers an upgrade. */
export function isAiBudgetFailure(code: KitAssetFailCode | null): boolean {
  return code === 'AI_BUDGET_EXHAUSTED';
}

const MASTER_COPY: Record<CatalogMasterFailReason, MessageDescriptor> = {
  decode: studioMessages.failMasterDecode,
  tooShort: studioMessages.failMasterTooShort,
  tooLong: studioMessages.failMasterTooLong,
};

/**
 * True when the failure shows the plain copy and the refund line (design s10): a full video, the
 * motion artwork, and the codes of the full videos.
 */
export function usesRefundLine(themeId: StudioThemeId | undefined, code: KitAssetFailCode | null): boolean {
  if (code !== null && NEW_CODES.includes(code)) {
    return true;
  }
  return (
    themeId !== undefined && isStudioThemeId(themeId) && (isFullVideoTheme(themeId) || themeId === 'motionArtwork')
  );
}

/**
 * True when the asset failed because the master of the song cannot be used: the artist must
 * replace the master in the catalog ("Try again" alone does not help).
 */
export function isMasterFailure(
  code: KitAssetFailCode | null,
  masterFailReason: CatalogMasterFailReason | null | undefined
): masterFailReason is CatalogMasterFailReason {
  return code === 'ANALYSIS_FAILED' && masterFailReason !== null && masterFailReason !== undefined;
}

/**
 * The copy of a failed asset (designs e3, m2b, n5, s10). A missing code reads as a stalled asset.
 * An analysis failure with a known master reason says that reason. With the asset type of a full
 * video or of the motion artwork, the copy has no credit sentence (see `kitFailText`). A code the
 * app does not know (an old row with `ARTWORK_TOO_SMALL`, which the server no longer writes) reads
 * as a plan failure.
 */
export function kitFailMessage(
  code: KitAssetFailCode | null,
  masterFailReason?: CatalogMasterFailReason | null,
  themeId?: StudioThemeId
): MessageDescriptor {
  if (isMasterFailure(code, masterFailReason)) {
    return MASTER_COPY[masterFailReason];
  }
  const key = code ?? 'STALLED';
  const table = themeId !== undefined && usesRefundLine(themeId, key) ? PLAIN_COPY : FAIL_COPY;
  const copy: MessageDescriptor | undefined = table[key];
  return copy ?? table.PLAN_FAILED;
}

/** The fields of a failed asset that its copy reads. */
export interface KitFailSubject {
  themeId: StudioThemeId;
  failCode: KitAssetFailCode | null;
  /** The credits that came back (the sum of the files of one run). */
  credits: number;
}

/**
 * The full copy of a failed asset (design s10): the reason, then "Your 3 credits came back." for a
 * full video, the motion artwork and the new codes. `refund: false` gives only the reason (a place
 * that shows the refund apart, such as the "Credit back" chip of Working on).
 */
export function kitFailText(
  intl: IntlShape,
  subject: KitFailSubject,
  masterFailReason?: CatalogMasterFailReason | null,
  options: { refund: boolean } = { refund: true }
): string {
  const reason = intl.formatMessage(kitFailMessage(subject.failCode, masterFailReason, subject.themeId));
  if (!options.refund || subject.credits <= 0 || !usesRefundLine(subject.themeId, subject.failCode)) {
    return reason;
  }
  return `${reason} ${intl.formatMessage(fullVideoProgressMessages.refund, { credits: subject.credits })}`;
}
